import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('projectType', formData.projectType);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5fr8fqeekkptfh8l3jg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', projectType: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="h-[600px] rounded-3xl overflow-hidden mb-12">
              <img
                src="https://public.readdy.ai/ai/img_res/9ab6f27bd22186de33b70db3d1b23589.jpg"
                alt="Electrical Team at Work"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="text-center space-y-2 mb-12">
              <h2 className="text-5xl leading-tight">
                <div className="text-gray-900 font-normal">Let's Power</div>
                <div className="text-gray-500 italic font-light" style={{ fontFamily: 'Georgia, serif' }}>Your Next Project</div>
                <div className="text-gray-900 font-bold">Together</div>
              </h2>
            </div>

            <div className="flex justify-center mb-16">
              <button 
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-12 py-5 bg-black text-white rounded-full text-lg font-medium hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Get Free Consultation
              </button>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-8 mb-16">
          <div className="text-center">
            <div className="w-12 h-12 bg-sky-500 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-phone-line text-2xl text-white"></i>
            </div>
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">PHONE</div>
            <div className="text-gray-900 font-medium">+91-8851640119</div>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-sky-500 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-mail-line text-2xl text-white"></i>
            </div>
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">EMAIL</div>
            <div className="text-gray-900 font-medium">prakashelectricalsopk2024@email.com</div>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-sky-500 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-map-pin-line text-2xl text-white"></i>
            </div>
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">LOCATION</div>
            <div className="text-gray-900 font-medium">India</div>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-sky-500 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-time-line text-2xl text-white"></i>
            </div>
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">HOURS</div>
            <div className="text-gray-900 font-medium">Mon - Sun: 10AM - 8PM</div>
          </div>
        </div>

        <div id="contact-form" className="max-w-3xl mx-auto bg-white rounded-3xl shadow-lg p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-2">Send Us a Message</h3>
          <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you shortly</p>

          <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="+91-8851640119"
                />
              </div>

              <div>
                <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-2">
                  Project Type *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Select project type</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="industrial">Industrial</option>
                  <option value="cable-laying">Cable Laying</option>
                  <option value="meter-installation">Meter Installation</option>
                  <option value="maintenance">Maintenance & Repair</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Project Details *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                placeholder="Tell us about your project requirements..."
              />
              <div className="text-xs text-gray-500 mt-1 text-right">
                {formData.message.length}/500 characters
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                Thank you! Your message has been sent successfully. We'll get back to you soon.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                Sorry, there was an error sending your message. Please try again or contact us directly.
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>

        <div className="mt-16 bg-gradient-to-r from-sky-500 to-sky-600 rounded-3xl p-8 md:p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">Meet Our Founder</h3>
          <div className="max-w-2xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-32 h-32 rounded-full overflow-hidden flex-shrink-0 border-4 border-white shadow-xl">
                <img
                  src="https://static.readdy.ai/image/fa99bb9c62df4d6cdaf96fd6599da80a/b56d2ec7e68787a122775f1ef5228e7f.jpeg"
                  alt="Ajay Kumar"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="text-left">
                <h4 className="text-2xl font-bold mb-2">Ajay Kumar</h4>
                <p className="text-sky-100 mb-4">Electrical Contractor & Founder</p>
                <p className="text-white/90 leading-relaxed">
                  A dedicated professional with strong technical expertise in electrical systems, project supervision, and client coordination. Known for reliability, attention to detail, and on-site problem-solving.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
